import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import { gql } from '@reverbdotcom/commons/src/gql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import PaypalLogo from '@reverbdotcom/cadence/images/vendor/paypal-logo.svg';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import { RCIcon, RCImage } from '@reverbdotcom/cadence/components';
import { WarningIcon, ReverbLogoCircleIcon, BankIcon } from '@reverbdotcom/cadence/icons/react';
import {
  DashboardSellingOrderPayoutDetailsQuery,
  DashboardSellingOrderPayoutDetailsQueryVariables,
  PayoutDetailsStatus,
  PayoutDetailsDestination,
} from '@reverbdotcom/commons/src/gql/graphql';

import { Paths } from '../../../components/shared/url_helpers';

const shopPoliciesEditPath = Paths.shopPoliciesEdit.expand({});
const myEarningsPayoutsPath = Paths.myEarningsPayouts.expand({});
const howToGetPaidPaypalArticlePath = Paths.helpCenterArticlesUrl.expand({ articleId: '41988411420699' });
const howLongToGetPaidArticlePath = Paths.helpCenterArticlesUrl.expand({ articleId: '360002778613' });

const COMPLETED = 'completed';
const INCOMPLETE = 'incomplete';
const INCOMPLETE_PAYOUT = 'incompletePayout';

const CONTENT_MAP = {
  [PayoutDetailsDestination.PAYPAL]: {
    completed: {
      hintArgs: {
        linkOpen: `<a href="${howToGetPaidPaypalArticlePath}">`,
        linkClose: '</a>',
      },
    },
    incomplete: {
      hintArgs: {
        linkOpen: `<a href="${howToGetPaidPaypalArticlePath}">`,
        linkClose: '</a>',
      },
      primaryActionText: 'editSettings',
      primaryActionPath: shopPoliciesEditPath,
    },
  },
  [PayoutDetailsDestination.ACH]: {
    completed: {
      primaryActionText: 'viewPayout',
      primaryActionPath: myEarningsPayoutsPath,
      hintArgs: {
        linkOpen: `<a href="${howLongToGetPaidArticlePath}">`,
        linkClose: '</a>',
      },
    },
    incomplete: {
      primaryActionText: 'editSettings',
      primaryActionPath: shopPoliciesEditPath,
    },
  },
  [PayoutDetailsDestination.REVERB]: {
    completed: {
      primaryActionText: 'addPayout',
      primaryActionPath: shopPoliciesEditPath,
    },
    incomplete: {
      primaryActionText: 'addPayout',
      primaryActionPath: shopPoliciesEditPath,
    },
  },
  [PayoutDetailsDestination.WALLET]: {
    completed: {
      primaryActionText: 'viewPayout',
      primaryActionPath: myEarningsPayoutsPath,
    },
    incomplete: {
      primaryActionText: 'editSettings',
      primaryActionPath: shopPoliciesEditPath,
    },
  },
} as any;

const ICON_MAP = {
  [PayoutDetailsDestination.ACH]: <BankIcon />,
  [PayoutDetailsDestination.REVERB]: <WarningIcon />,
  [PayoutDetailsDestination.WALLET]: <ReverbLogoCircleIcon />,
};

function PayoutTypeBadge({ payoutDestination }: { payoutDestination: PayoutDetailsDestination }) {
  const normalizedPayoutType = payoutDestination.toString().toLowerCase();

  const badgeTypeClass = classNames('payout-details__status-tag', {
    'payout-details__status-tag--paypal': payoutDestination === PayoutDetailsDestination.PAYPAL,
    'payout-details__status-tag--ach': payoutDestination === PayoutDetailsDestination.ACH,
    'payout-details__status-tag--reverb': payoutDestination === PayoutDetailsDestination.REVERB,
    'payout-details__status-tag--wallet': payoutDestination === PayoutDetailsDestination.WALLET,
  });

  return (
    <div className={badgeTypeClass}>
      {normalizedPayoutType === 'paypal' ? (
        <RCImage src={PaypalLogo} imageBox="none" />
      ) : (
        <RCIcon icon={ICON_MAP[payoutDestination]} size="inline" />
      )} {I18n.t(`selling.order.payoutDetail.payoutTypeBadge.${normalizedPayoutType}`)}
    </div>
  );
}

interface PayoutTypePrimaryActionProps {
  payoutDestination: PayoutDetailsDestination;
  normalizedPayoutStatus: typeof COMPLETED | typeof INCOMPLETE;
}

function PayoutTypePrimaryAction({
  payoutDestination,
  normalizedPayoutStatus,
}: PayoutTypePrimaryActionProps) {
  const primaryAction = CONTENT_MAP[payoutDestination][normalizedPayoutStatus];

  if (!primaryAction?.primaryActionText) return null;

  return (
    <CoreLink
      to={primaryAction?.primaryActionPath}
      className="button button--mini button--offwhite mt-4"
    >
      {I18n.t(`selling.order.payoutDetail.payoutTypePrimaryAction.${primaryAction?.primaryActionText}`)}
    </CoreLink>
  );
}

interface HintTextStatusArgs {
  hasPayout: boolean;
  payoutDestination: PayoutDetailsDestination;
  normalizedPayoutStatus: typeof COMPLETED | typeof INCOMPLETE;
}

// some PayPal orders have an actual payout, and some are paid instantly to the seller.
// the "incomplete" text for PayPal sellers is different in these two cases.
function getHintTextStatus({ normalizedPayoutStatus, hasPayout, payoutDestination }: HintTextStatusArgs) {
  if (hasPayout && normalizedPayoutStatus === INCOMPLETE && payoutDestination === PayoutDetailsDestination.PAYPAL) {
    return INCOMPLETE_PAYOUT;
  }

  return normalizedPayoutStatus;
}

interface PayoutDetailsProps {
  orderUuid: string;
}

export default function PayoutDetails({ orderUuid }: PayoutDetailsProps) {
  const { data, loading } = useQuery<DashboardSellingOrderPayoutDetailsQuery, DashboardSellingOrderPayoutDetailsQueryVariables>(
    SELLER_ORDER_PAYOUT_DETAILS,
    {
      variables: {
        orderUuid,
      },
    },
  );

  const { sellerOrder } = data?.order || {};
  const { payoutDetails } = sellerOrder || {};
  const { status: payoutStatus, destination: payoutDestination } = payoutDetails || {};

  if (loading || !payoutDestination) {
    return null;
  }

  // we do not return payout status steps for non-payoutable orders or orders that have been fully refunded or cancelled
  const hasPayout = sellerOrder?.payoutStatusSteps?.length > 0;

  const normalizedPayoutStatus = payoutStatus === PayoutDetailsStatus.PAYMENT_INITIATED ? COMPLETED : INCOMPLETE;

  const hintTextStatus = getHintTextStatus({ normalizedPayoutStatus, hasPayout, payoutDestination });

  return (
    <div>
      <h4 className="weight-bold mb-2">
        {I18n.t(`selling.order.payoutDetail.title.${normalizedPayoutStatus}`)}
      </h4>
      <PayoutTypeBadge payoutDestination={payoutDestination} />
      <Translate
        tag="div"
        className="hint"
        text={`selling.order.payoutDetail.hintText.${payoutDestination.toString().toLowerCase()}.${hintTextStatus}`}
        args={CONTENT_MAP[payoutDestination][normalizedPayoutStatus]?.hintArgs}
        html={!!CONTENT_MAP[payoutDestination][normalizedPayoutStatus]?.hintArgs}
      />
      <PayoutTypePrimaryAction payoutDestination={payoutDestination} normalizedPayoutStatus={normalizedPayoutStatus} />
    </div>
  );
}

export const SELLER_ORDER_PAYOUT_DETAILS = gql(`
  query DashboardSellingOrderPayoutDetails($orderUuid: String) {
    order(input: { uuid: $orderUuid }) {
      _id
      sellerOrder {
        payoutDetails {
          status
          destination
        }
        payoutStatusSteps {
          label
        }
      }
    }
  }
`);
